import { Component, DestroyRef, computed, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';

import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorComissaoTipo } from '@/core/api/sicom-api/registro/prestador/prestador-comissoes.model';
import { PrestadorCorpoClinicoMedicoResponse } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.model';
import { PrestadorComposicaoService } from '@/core/api/sicom-api/registro/prestador/prestador-composicao.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Diálogo de seleção de médico escopado ao corpo clínico ativo do prestador. Carrega a lista completa
 * ao abrir e filtra por nome/CRM no cliente. Linhas não selecionáveis (médico inativo ou com débito)
 * permanecem visíveis, porém desabilitadas, com o motivo do bloqueio.
 */
@Component({
    selector: 'app-seletor-medico-corpo-clinico',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, Button, Dialog, InputText, TableModule, Tooltip],
    templateUrl: './seletor-medico-corpo-clinico.html'
})
export class SeletorMedicoCorpoClinico {
    readonly showDialog = model<boolean>(false);
    readonly idPrestador = input.required<number>();
    readonly ufPrestador = input.required<string>();
    readonly excluirMembrosComissaoAtivos = input<PrestadorComissaoTipo | null>(null);
    readonly titulo = input<string>('Selecionar médico do corpo clínico');
    readonly medico = output<PessoaDomain>();

    readonly carregando = signal(false);
    readonly medicos = signal<PrestadorCorpoClinicoMedicoResponse[]>([]);
    readonly filtro = signal('');
    readonly selecionado = signal<PrestadorCorpoClinicoMedicoResponse | undefined>(undefined);

    readonly medicosFiltrados = computed<PrestadorCorpoClinicoMedicoResponse[]>(() => {
        const termo = this.normalizar(this.filtro());
        if (!termo) {
            return this.medicos();
        }
        return this.medicos().filter((medico) => this.normalizar(medico.nome).includes(termo) || this.normalizar(medico.crm).includes(termo));
    });

    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorComposicaoService);

    aoAbrir(): void {
        this.filtro.set('');
        this.selecionado.set(undefined);
        this.carregar();
    }

    selecionarLinha(medico: PrestadorCorpoClinicoMedicoResponse): void {
        if (medico.selecionavel) {
            this.selecionado.set(medico);
        }
    }

    confirmar(): void {
        const escolhido = this.selecionado();
        if (!escolhido?.selecionavel) {
            return;
        }
        this.medico.emit({
            id: escolhido.idMedico,
            uf: escolhido.ufMedico,
            crm: escolhido.crm ?? undefined,
            nome: escolhido.nome ?? undefined,
            tipo: 'MEDICO'
        });
        this.fechar();
    }

    fechar(): void {
        this.showDialog.set(false);
    }

    private carregar(): void {
        const id = this.idPrestador();
        const uf = this.ufPrestador();
        if (!id || !uf) {
            this.medicos.set([]);
            return;
        }
        this.carregando.set(true);
        this.service
            .buscarMedicosDoCorpoClinico({ id, uf, excluirMembrosComissaoAtivos: this.excluirMembrosComissaoAtivos() })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe((medicos) => this.medicos.set(medicos));
    }

    private normalizar(valor?: string | null): string {
        return String(valor ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    }
}
